import { useCallback, useEffect, useState } from 'react'

import { Table } from '@loadsmart/loadsmart-ui'
import { Layout, Text, Tag, type TagVariant } from '@loadsmart/miranda-react'

import type { RequestStatus } from 'core/domain/Request'
import { AsyncContent } from 'core/ui/async-content/AsyncContent'
import { LoadingState } from 'core/ui/async-content/LoadingState'
import { useAbortController } from 'core/ui/hooks/useAbortController'
import { formatCurrency } from 'core/utils/currency'

import type { Shipment } from '../domain/Shipment'
import type { ShipmentCharge, ShipmentChargeStatus } from '../domain/ShipmentCharges'
import { formatChargesDate } from '../presenters/shipmentChargePresenter'
import { CancelChargeDialog } from './CancelChargeDialog'
import { ExpandableRowContent } from './ChargesTableExpandableRow'
import { EmptyShipmentChargesState } from './EmptyShipmentChargesState'
import { ErrorShipmentChargesState } from './ErrorShipmentChargesState'
import type { ShipmentChargesColumnKey, SortDescriptor } from './hooks/useShipmentCharges'
import { ChargesStyledTable } from './ShipmentChargesTable.styles'

const ChargesTableColumns: {
  key: ShipmentChargesColumnKey
  label: string
  class: string
}[] = [
  { key: 'type', label: 'Type', class: 'all-media-view' },
  { key: 'createdBy', label: 'Added by', class: 'desktop-view-only' },
  { key: 'createdAt', label: 'Creation date', class: 'desktop-view-only' },
  { key: 'status', label: 'Status', class: 'all-media-view' },
  { key: 'total', label: 'Amount', class: 'all-media-view' },
]

function getShipmentChargeStatusTagVariant(status: ShipmentChargeStatus): TagVariant {
  switch (status) {
    case 'approved':
      return 'success'
    case 'rejected':
      return 'warning'
    case 'canceled':
      return 'danger'
    case 'pending':
    default:
      return 'neutral'
  }
}

type CancelChargeDialogStatus = {
  open: boolean
  mounted: boolean
  chargeUUID: string | undefined
}

export type ShipmentChargesTableProps = {
  shipment: Shipment
  shipmentCharges: ShipmentCharge[]
  shipmentChargesStatus: RequestStatus
  sortDescriptor: SortDescriptor | undefined
  onSort: (column: ShipmentChargesColumnKey) => void
  fetchShipmentCharges: (shipmentUUID: string, abortController: AbortController) => Promise<void>
  refetchShipmentCharges: (shipmentUUID: string) => Promise<void>
  onUpdateCharge: (charge?: ShipmentCharge) => void
}

export function ShipmentChargesTable({
  shipment,
  shipmentCharges,
  shipmentChargesStatus,
  sortDescriptor,
  onSort,
  fetchShipmentCharges,
  refetchShipmentCharges,
  onUpdateCharge,
}: ShipmentChargesTableProps) {
  const abortController = useAbortController([shipment.uuid])

  useEffect(() => {
    fetchShipmentCharges(shipment.uuid, abortController)

    return () => {
      abortController.abort()
    }
  }, [shipment.uuid, fetchShipmentCharges, abortController])

  const [cancelChargeDialogStatus, setCancelChargeDialogStatus] =
    useState<CancelChargeDialogStatus>({
      open: false,
      mounted: false,
      chargeUUID: undefined,
    })

  function handleCancelCharge(chargeUUID: string) {
    setCancelChargeDialogStatus({ open: true, mounted: true, chargeUUID: chargeUUID })
  }

  const onCancelChargeDialogClose = useCallback(() => {
    setCancelChargeDialogStatus(prevStatus => ({
      ...prevStatus,
      open: false,
    }))
  }, [])

  const onCancelChargeDialogClosed = useCallback(() => {
    setCancelChargeDialogStatus(prevStatus => ({
      ...prevStatus,
      mounted: false,
      chargeUUID: undefined,
    }))
  }, [])

  return (
    <AsyncContent
      emptyState={<EmptyShipmentChargesState />}
      errorState={
        <ErrorShipmentChargesState
          onRefetchCharges={() => {
            refetchShipmentCharges(shipment.uuid)
          }}
        />
      }
      loadingState={<LoadingState />}
      status={shipmentChargesStatus}
      count={shipmentCharges.length}
    >
      <ChargesStyledTable>
        <Table.Head>
          <Table.Row>
            <Table.HeadCell />
            {ChargesTableColumns.map(column => (
              <Table.HeadCell
                className={column.class}
                key={column.key}
                onClick={() => onSort(column.key)}
              >
                {column.label}
                <Table.SortHandle
                  order={
                    sortDescriptor?.column === column.key ? sortDescriptor.direction : undefined
                  }
                />
              </Table.HeadCell>
            ))}
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {shipmentCharges?.map(charge => (
            <Table.ExpandableRow
              key={charge.uuid}
              expandableContent={
                <ExpandableRowContent charge={charge} onCancelCharge={handleCancelCharge} />
              }
            >
              <Table.Cell>
                <Text variant="body-md">{charge.type?.name}</Text>
              </Table.Cell>
              <Table.Cell className="desktop-view-only">
                <Text variant="body-md">{charge.createdBy}</Text>
              </Table.Cell>
              <Table.Cell className="desktop-view-only">
                <Text variant="body-md">
                  {charge.createdAt ? formatChargesDate(charge.createdAt) : '-'}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Tag variant={getShipmentChargeStatusTagVariant(charge.status.value)}>
                  {charge.status.label}
                </Tag>
              </Table.Cell>
              <Table.Cell>
                <Layout.Stack gap="spacing-0-5">
                  <Text variant="body-md">{formatCurrency(charge.total, true)}</Text>
                  <Text variant="body-sm" color="color-text-tertiary">
                    {charge.quantity} x {formatCurrency(charge.unitPrice, true)}
                  </Text>
                </Layout.Stack>
              </Table.Cell>
            </Table.ExpandableRow>
          ))}
        </Table.Body>
      </ChargesStyledTable>
      <div />
      {cancelChargeDialogStatus.mounted && cancelChargeDialogStatus.chargeUUID && (
        <CancelChargeDialog
          isOpen={cancelChargeDialogStatus.open}
          onClose={onCancelChargeDialogClose}
          onClosed={onCancelChargeDialogClosed}
          shipment={shipment}
          chargeUUID={cancelChargeDialogStatus.chargeUUID}
          onUpdateCharge={onUpdateCharge}
        />
      )}
    </AsyncContent>
  )
}
